export type DatetimeFormatInference = {
  format: string;
  example: string;
  matchCount: number;
};

const COMMON_FORMAT_LABELS: Record<string, string> = {
  "%Y-%m-%d %H:%M:%S %Z": "2024-01-15 15:30:45 EEST — date, time, timezone abbreviation",
  "%Y-%m-%d %H:%M:%S": "2024-01-15 15:30:45 — year, month, day",
  "%Y-%m-%d %H:%M": "2024-01-15 15:30 — year, month, day (no seconds)",
  "%Y-%m-%dT%H:%M:%S": "2024-01-15T15:30:45 — ISO with T (not a space)",
};

/** Infer a Python strptime pattern from one timestamp cell value. */
export function inferDatetimeFormatFromSample(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} [A-Z]{2,5}$/.test(trimmed)) {
    return "%Y-%m-%d %H:%M:%S %Z";
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return "%Y-%m-%d %H:%M:%S%z";
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return "%Y-%m-%d %H:%M:%S";
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(trimmed)) {
    return "%Y-%m-%d %H:%M";
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed)) {
    return "%Y-%m-%dT%H:%M:%S";
  }
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    const first = Number(trimmed.slice(0, 2));
    return first > 12 ? "%d/%m/%Y %H:%M:%S" : "%m/%d/%Y %H:%M:%S";
  }
  if (/^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return "%d.%m.%Y %H:%M:%S";
  }
  if (/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    const first = Number(trimmed.slice(0, 2));
    return first > 12 ? "%d-%m-%Y %H:%M:%S" : "%m-%d-%Y %H:%M:%S";
  }
  if (/^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return "%Y/%m/%d %H:%M:%S";
  }

  return null;
}

/** Pick the most common strptime pattern across sample timestamp cells. */
export function inferDatetimeFormatFromSamples(values: string[]): DatetimeFormatInference | null {
  const tallies = new Map<string, { example: string; count: number }>();

  for (const value of values) {
    const format = inferDatetimeFormatFromSample(value);
    if (!format) continue;
    const trimmed = value.trim();
    const existing = tallies.get(format);
    if (existing) {
      existing.count += 1;
    } else {
      tallies.set(format, { example: trimmed, count: 1 });
    }
  }

  let best: DatetimeFormatInference | null = null;
  for (const [format, { example, count }] of Array.from(tallies.entries())) {
    if (!best || count > best.matchCount) {
      best = { format, example, matchCount: count };
    }
  }

  return best;
}

export function labelForDatetimeFormat(format: string, example?: string): string {
  const presetLabel = COMMON_FORMAT_LABELS[format];
  if (presetLabel) return presetLabel;
  if (example) {
    return `Matches timestamps like "${example}"`;
  }
  return format;
}

/** When server expected format disagrees with the raw value, infer from the value. */
export function resolveDatetimeFormatFix(rawValue: string, configuredFormat?: string): string | null {
  const inferred = inferDatetimeFormatFromSample(rawValue);
  if (!inferred) return null;
  if (configuredFormat?.trim() === inferred) return null;
  return inferred;
}

export function timestampFormatNeedsTimezone(format: string): boolean {
  return /%z|%Z|%\+/.test(format);
}

export function sampleValueHasTimezoneSuffix(value: string): boolean {
  return /\s[A-Z]{2,5}$/.test(value.trim()) || /[+-]\d{2}:?\d{2}$/.test(value.trim());
}

/** True when configured format likely cannot parse the sample timestamps. */
export function datetimeFormatConflictsWithSamples(
  format: string,
  sampleValues: string[]
): DatetimeFormatInference | null {
  const trimmedFormat = format.trim();
  if (!trimmedFormat || !sampleValues.length) return null;

  const inferred = inferDatetimeFormatFromSamples(sampleValues);
  if (!inferred || inferred.format === trimmedFormat) return null;

  const samplesHaveTz = sampleValues.some(sampleValueHasTimezoneSuffix);
  const formatHasTz = timestampFormatNeedsTimezone(trimmedFormat);
  if (samplesHaveTz && !formatHasTz) return inferred;
  if (inferred.matchCount >= 2) return inferred;

  return null;
}
